import { useState, useEffect, type FC } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import { RepositoryOption } from '../hooks/useRepositoryData';

const StyledRepositoryOption = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 0),
  width: '100%',
}));

const RepositoryHeader = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}));

const LeftContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
}));

const RightContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  marginLeft: '8px',
}));

const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  alignItems: 'flex-end',
}));

const SystemDivider = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

interface RepositorySelectorProps {
  value: string;
  onChange: (value: string) => void;
  repositories: RepositoryOption[];
  loading: boolean;
  error: string | null;
  onSearch: (searchTerm: string) => RepositoryOption[];
  disabled?: boolean;
  placeholder?: string;
}

export const RepositorySelector: FC<RepositorySelectorProps> = ({
  value,
  onChange,
  repositories,
  loading,
  error,
  onSearch,
  disabled = false,
  placeholder = 'Search repositories by name or fullName...',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] =
    useState<RepositoryOption[]>(repositories);

  // Update search results when input changes
  useEffect(() => {
    const results = onSearch(inputValue);
    setSearchResults(results);
  }, [inputValue, onSearch]);

  // Find the current selected repository
  const selectedRepository =
    repositories.find(repo => repo.value === value) || null;

  // Create options with dividers for rendering
  const createOptionsWithDividers = (repos: RepositoryOption[]) => {
    const options: (RepositoryOption | { isDivider: true; system: string })[] =
      [];

    if (inputValue) {
      // For search results, just return the repositories
      return repos;
    }

    // Group the repositories by system
    const grouped = repos.reduce(
      (acc, repo) => {
        if (!acc[repo.system]) {
          acc[repo.system] = [];
        }
        acc[repo.system].push(repo);
        return acc;
      },
      {} as { [system: string]: RepositoryOption[] },
    );

    // Sort systems alphabetically
    const sortedSystems = Object.keys(grouped).sort();

    sortedSystems.forEach((system, index) => {
      if (index > 0) {
        options.push({ isDivider: true, system });
      }
      options.push(...grouped[system]);
    });

    return options;
  };

  const optionsWithDividers = createOptionsWithDividers(searchResults);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading repositories: {error}
      </Alert>
    );
  }

  return (
    <Autocomplete
      value={selectedRepository}
      onChange={(_, newValue) => {
        if (newValue && 'value' in newValue) {
          onChange(newValue.value);
        } else {
          onChange('');
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={optionsWithDividers}
      filterOptions={opts => opts as any}
      getOptionLabel={option => {
        if ('isDivider' in option) return '';
        return `Repository: ${option.displayName}`;
      }}
      isOptionEqualToValue={(option, optValue) => {
        if ('isDivider' in option) return false;
        if (!optValue || 'isDivider' in (optValue as any)) return false;
        return (option as any).value === (optValue as any).value;
      }}
      loading={loading}
      disabled={disabled}
      fullWidth
      renderInput={params => (
        <TextField
          {...params}
          label="Repository"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        if ('isDivider' in option) {
          return (
            <SystemDivider key={`divider-${option.system}`}>
              <Typography variant="subtitle2" color="textSecondary">
                System: {option.system}
              </Typography>
            </SystemDivider>
          );
        }

        return (
          <li {...props} key={option.value}>
            <StyledRepositoryOption>
              <RepositoryHeader>
                <LeftContent style={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      lineHeight: 1.1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {`Repository: ${option.displayName}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ lineHeight: 1.1 }}
                  >
                    {option.fullName}
                  </Typography>
                </LeftContent>
                <RightContent>
                  <ChipContainer>
                    <Chip
                      label={`Tenant: ${option.system}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        minHeight: 20,
                      }}
                    />
                    <Chip
                      label={`Github Org: ${option.owner}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        minHeight: 20,
                      }}
                    />
                  </ChipContainer>
                </RightContent>
              </RepositoryHeader>
            </StyledRepositoryOption>
          </li>
        );
      }}
      noOptionsText={(() => {
        if (loading) return 'Loading repositories...';
        if (inputValue) return 'No repositories found matching your search';
        return 'No repositories available';
      })()}
    />
  );
};
